import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type {
	LeasingDetailDataDTO,
	LoanDetailDataDTO,
	LoanForBusinessDetailDataDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { DetailLoader } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLoader";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { DetailValueChart } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailValueChart";
import { RelatedSectionRedesign } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedSectionRedesign";
import { CoDebtorsList } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/CoDebtorsList";
import { LoanAllBasicInfoDetails } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/LoanAllBasicInfoDetails";
import { PortfolioBasicInfoRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/PortfolioBasicInfoRows";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { maturityFormatter } from "web_myplann/Portfolio/Helpers/maturityFormatter";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

export const LoanAllRedesign: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailLoader hasChart />;
	}

	const { daysData, totalValue, totalInvestedValue, toDate, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const portfolioData = baseData.data as LoanForBusinessDetailDataDTO &
		LoanDetailDataDTO &
		LeasingDetailDataDTO;

	const interestRate = portfolioData.interestRate
		? `${portfolioData.interestRate} % p.a.`
		: EMPTY_DETAIL_FIELD;

	const installment = portfolioData.installment
		? getPortfolioFrequencyValue(
				portfolioData.installment ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	return (
		<>
			{daysData.length > 0 && (
				<DetailValueChart
					loanChart
					totalValue={totalValue}
					totalInvestedValue={totalInvestedValue}
					totalEvaluationPercent={
						reporting?.summary?.totalEvaluationPercent ?? 0
					}
					daysData={daysData}
					currencySign={currencySign}
					valueDate={toDate}
					showChartInfo={showChartInfo}
					bottomComponent={
						<Stack>
							<DetailRow
								label={t(PageResources.Loan.amount)}
								value={moneyFormatter(
									portfolioData.originalObligationAmount,
									currencySign,
								)}
							/>
							<DetailRow
								label={t(PageResources.Loan.matury)}
								value={maturityFormatter(baseData.maturityLengthMonths ?? 0, t)}
							/>
							<DetailRow
								label={t(PageResources.Loan.interestRate)}
								value={interestRate}
							/>
							<DetailRow
								label={t(PageResources.Loan.installment)}
								value={installment}
							/>
						</Stack>
					}
				/>
			)}

			<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
				<PortfolioBasicInfoRows baseData={baseData} />
			</DetailCard>
			<CoDebtorsList coDebtors={portfolioData.coDebtors ?? []} />
			<LoanAllBasicInfoDetails
				baseData={baseData}
				fixationEndDate={portfolioData.fixationEndDate}
			/>

			<RelatedSectionRedesign
				documents={documents?.data}
				related={baseData.related}
				showActions={baseData.allowEditByClient}
				itemKind={PortfolioItemKind.Portfolio}
				portfolioId={baseData.id}
			/>
		</>
	);
};
